



































import { computed, defineComponent, ref } from "@vue/composition-api";
import { format } from "date-fns";
import { InputFile } from "@scrile/api-provider/dist/api/FileProvider";
import {
  InputPersonalInformation,
  PersonalInformation,
} from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import { UserVerification } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import FileUploader, { showDefaultUploadError } from "@/lib/FileUploader";
import { FormValues } from "@/types";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseForm from "@/components/BaseForm";
import BaseInputFile from "@/components/BaseInputFile";
import LoadingThumbnail from "@/components/ContentLoadingPlaceholders/components/LoadingThumbnail.vue";
import PayeeInfoInputFile from "./PayeeInfoInputFile.vue";
import { personalIdFields } from "../config";

export default defineComponent({
  name: "PersonalIdForm",
  components: {
    BaseInputFile,
    LoadingThumbnail,
    BaseCheckbox,
    PayeeInfoInputFile,
    BaseForm,
  },
  setup(_, { emit }) {
    const { authUserStrict, updateUser } = useAuthUser();
    const { search } = useUserVerification();
    const { deleteFile, getUploadUrl } = useFiles();
    const userVerificationStatus = ref<UserVerification | null>(null);
    const personalInfo = ref<PersonalInformation | null>(null);

    const progress = ref<Record<string, { progress: number; isLoading: boolean }>>({
      id_photo: {
        progress: 100,
        isLoading: false,
      },
      photo_with_id: {
        progress: 100,
        isLoading: false,
      },
    });

    const fields = computed(() => personalIdFields());

    const values = ref<FormValues>({
      dateOfBirth: personalInfo.value?.dateOfBirth
        ? format(new Date(personalInfo.value.dateOfBirth), "yyyy-MM-dd")
        : undefined,
      id_photo: {
        data: userVerificationStatus.value?.idPhoto || null,
      },
      photo_with_id: {
        data: userVerificationStatus.value?.photoWithId || null,
      },
    });

    const getUserVerification = async () => {
      let data = await search({ userIds: [authUserStrict.value.id] });
      userVerificationStatus.value = data.result.find(i => i.userId === authUserStrict.value.id) || null;
      values.value.id_photo = {
        data: userVerificationStatus.value?.idPhoto || null,
      };
      values.value.photo_with_id = {
        data: userVerificationStatus.value?.photoWithId || null,
      };
    };

    const addFileToQueue = async (type: string, file: File): Promise<any> => {
      return new Promise<any>(async (resolve, reject) => {
        const response = await getUploadUrl([{ subjectId: authUserStrict.value.id, type }]);

        FileUploader.addToQueue([
          {
            file: file,
            fileType: getFileMimeType(file),
            id: `${response[0].fileId.subjectId}_${response[0].fileUpload.uuid}`,
            urlPart: response[0].fileUpload.urlPart,
            onSuccess: async () => {
              progress.value[type].isLoading = false;
              progress.value[type].progress = 100;
              resolve(response[0]);
            },
            onUploadProgress: uploadProgress => {
              const res = ((uploadProgress?.loaded ?? 0) / (uploadProgress?.total ?? 0)) * 100;
              progress.value[type].progress = res === 100 ? 99.9 : res;
            },
            onError: e => {
              showDefaultUploadError();
              reject(e);
            },
          },
        ]);
      });
    };
    const checkValid = () => {
      const container = fields.value.find(el => el.name === "photo_container");

      if (!values.value.dateOfBirth) {
        return emit("setValid", false);
      }
      if (!container) return emit("setValid", true);

      if (
        container.value.find((i: { name: string }) => i.name === "id_photo")?.meta?.required &&
        !userVerificationStatus.value?.idPhoto
      ) {
        return emit("setValid", false);
      }
      if (
        container.value.find((i: { name: string }) => i.name === "photo_with_id")?.meta?.required &&
        !userVerificationStatus.value?.photoWithId
      ) {
        return emit("setValid", false);
      }
      return emit("setValid", true);
    };

    const handleDelete = async (type: string) => {
      await deleteFile({ fileId: { subjectId: authUserStrict.value.id, type: type } });
      await getUserVerification();
      checkValid();
    };

    const upload = async (file: FileList, type: string) => {
      progress.value[type].progress = 0;
      progress.value[type].isLoading = true;
      const uploadData = await addFileToQueue(type, file[0]);
      const data: InputFile = {
        fileId: uploadData.fileId,
        uuid: uploadData.fileUpload.uuid,
      };
      await providers.FileProvider.update({ data });
      await getUserVerification();
      checkValid();
    };

    const getPersonalInfo = async () => {
      personalInfo.value = await providers.PersonalInformationProvider.find({ id: authUserStrict.value.id });
      values.value.dateOfBirth = personalInfo.value.dateOfBirth
        ? format(new Date(personalInfo.value.dateOfBirth), "yyyy-MM-dd")
        : null;
    };

    const getData = async () => {
      await Promise.all([getUserVerification(), getPersonalInfo()]);
      checkValid();
    };

    const submitDateOfBirth = async () => {
      if (!values.value || !values.value.dateOfBirth) return;
      const data: InputPersonalInformation = {
        userId: authUserStrict.value.id,
        dateOfBirth: new Date(values.value.dateOfBirth).toISOString(),
      };
      personalInfo.value = await providers.PersonalInformationProvider.update({ data });
      updateUser({
        ...authUserStrict.value,
        personalInfo: {
          ...authUserStrict.value.personalInfo,
          dateOfBirth: personalInfo.value.dateOfBirth,
        } as PersonalInformation,
      });
    };

    const created = async () => {
      emit("setValid", true);
      emit("setCb", () => submitDateOfBirth());
      try {
        emit("setLoading", true);
        await getData();
      } finally {
        emit("setLoading", false);
      }
    };

    created();

    return {
      values,
      fields,
      progress,
      upload,
      handleDelete,
      checkValid,
    };
  },
});
